/* RevealableNumber — bank-style masked number with password-gated reveal.

   Mirrors the Chase / Mercury / Brex pattern:
     • Always rendered masked: ••••••••1234
     • Click the eye icon to open an inline password prompt
     • On success, full digits are shown for `AUTO_HIDE_SECONDS`, then
       the value re-masks automatically (countdown is visible).
     • A small "Hide" affordance lets the user collapse early.
     • The successful reveal call is audit-logged server-side.

   Props
     methodId       — payment method id (required)
     field          — which sensitive field to surface from the reveal
                       payload: "account_number" | "routing_number"
                       | "wire_routing_number" | "iban"
                       | "intermediary_account_number" | "swift_bic"
     last4          — last 4 digits to render in the masked state
     digitsHint     — overall digit count hint (drives mask length)
     label          — small label rendered above the value (optional)
     testIdPrefix   — used for data-testid composition
*/
import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Lock, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const AUTO_HIDE_SECONDS = 30;

const FIELD_LABELS = {
  account_number:             "Account #",
  routing_number:             "ACH routing #",
  wire_routing_number:        "Wire routing #",
  iban:                       "IBAN",
  intermediary_account_number: "Intermediary acct #",
  swift_bic:                  "SWIFT / BIC",
};

function maskNumber(last4, digitsHint = 12) {
  const clean = String(last4 || "").replace(/\D/g, "").slice(-4) || "----";
  const dots = "•".repeat(Math.max(4, Math.min(16, digitsHint - 4)));
  return `${dots}${clean}`;
}

export default function RevealableNumber({
  methodId,
  field,
  last4,
  digitsHint = 12,
  label,
  testIdPrefix = "reveal",
}) {
  const [open, setOpen]         = useState(false);    // password prompt visible
  const [busy, setBusy]         = useState(false);
  const [password, setPassword] = useState("");
  const [value, setValue]       = useState(null);     // revealed clear value
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timerRef = useRef(null);

  const startHideTimer = () => {
    setSecondsLeft(AUTO_HIDE_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          setValue(null);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const collapse = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setValue(null);
    setSecondsLeft(0);
    setOpen(false);
    setPassword("");
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/payment-methods/${methodId}/reveal`, { password });
      const v = data?.[field];
      if (!v) {
        toast.error("No value on file for this field.");
        collapse();
        return;
      }
      setValue(v);
      setOpen(false);
      setPassword("");
      startHideTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reveal — check your password.");
    } finally {
      setBusy(false);
    }
  };

  const valueShown = value || maskNumber(last4, digitsHint);
  const labelText  = label || FIELD_LABELS[field] || "Number";

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-${field}`}>
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
        {labelText}
      </div>
      <div className="flex items-center gap-2">
        <code
          className={`font-mono text-[13px] px-2.5 py-1 rounded-md border border-foreground/15 bg-secondary/40 select-all ${
            value ? "text-foreground" : "text-foreground/60 tracking-widest"
          }`}
          data-testid={`${testIdPrefix}-${field}-value`}
        >
          {valueShown}
        </code>
        {value ? (
          <>
            <span className="text-[10px] mono text-foreground/55" data-testid={`${testIdPrefix}-${field}-countdown`}>
              auto-hide {secondsLeft}s
            </span>
            <button
              type="button"
              onClick={collapse}
              className="inline-flex items-center gap-1 text-[11px] text-foreground/60 hover:text-foreground"
              data-testid={`${testIdPrefix}-${field}-hide`}
            >
              <EyeOff className="w-3.5 h-3.5"/>Hide
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            className="inline-flex items-center gap-1 text-[11px] text-foreground/60 hover:text-foreground"
            data-testid={`${testIdPrefix}-${field}-show`}
          >
            <Eye className="w-3.5 h-3.5"/>Show
          </button>
        )}
      </div>
      {open && (
        <form
          onSubmit={submit}
          className="flex items-center gap-2 mt-1.5 rounded-lg border border-foreground/20 bg-white p-2"
          data-testid={`${testIdPrefix}-${field}-prompt`}
        >
          <Lock className="w-3.5 h-3.5 text-foreground/50 ml-1 shrink-0"/>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Re-enter your password to reveal"
            data-testid={`${testIdPrefix}-${field}-password`}
            className="flex-1 min-w-0 h-8 px-2 text-[12.5px] outline-none bg-transparent"
          />
          <button
            type="submit"
            disabled={busy || !password}
            data-testid={`${testIdPrefix}-${field}-confirm`}
            className="h-8 px-3 rounded-full bg-foreground text-background text-[11.5px] disabled:opacity-50"
          >
            {busy ? "…" : "Reveal"}
          </button>
          <button
            type="button"
            onClick={collapse}
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-secondary"
            aria-label="Cancel"
            data-testid={`${testIdPrefix}-${field}-cancel`}
          >
            <X className="w-3.5 h-3.5 text-foreground/55"/>
          </button>
        </form>
      )}
    </div>
  );
}
